<script setup lang="ts">
import type { NavigationMenuItem } from '#ui/types';
import lodashIsArray from 'lodash-es/isArray';

// COMPOSABLES

const { tt } = useTypedI18n();
const { isLoggedIn, data, logout } = useAuthUtils();
const { isMobile } = useResponsive();
const { headerMenuItems } = await useStoryblokGlobalConfig();
const accMenuItems = useAccountMenuItems();
const { openAuthModal } = useModals();
const { trackLoginSignupButton, trackLogout } = useTracking();

// DATA
const navigationMenuItems = computed(() => mapMenuItems(headerMenuItems.value));
const accountMenuItems = computed(() => mapMenuItems(accMenuItems.value));
const openModel = defineModel<boolean>('open', { required: true });
const transitionClass = 'transition duration-300';
const closeTransition = ref<boolean>(true);
const navigationMenuUi = { list: 'space-y-2', link: 'px-2 py-2' };

// WATCHERS
watch(isMobile, val => val || immediateClose());

// METHODS
const mapMenuItems = (items: NavigationMenuItem[] | NavigationMenuItem[][]): NavigationMenuItem[] =>
    items.map(item =>
        lodashIsArray(item)
            ? mapMenuItems(item)
            : {
                  ...item,
                  onSelect: makeOnSelectMenuItemHandler(item),
                  ...(item.children
                      ? {
                            children: item.children.map(child => ({
                                ...child,
                                onSelect: makeOnSelectMenuItemHandler(child),
                            })),
                        }
                      : { trailingIcon: 'lucide:chevron-right' }),
              }
    );

const immediateClose = (): void => {
    closeTransition.value = false;
    openModel.value = false;
};

const resetCloseTransition = (): void => {
    closeTransition.value = true;
};

const withMenuClose = (handler: Function): void => {
    immediateClose();
    handler();
};

const makeOnSelectMenuItemHandler =
    (item: NavigationMenuItem) =>
    (e: Event): void =>
        withMenuClose(() => item.onSelect?.(e));

// HANDLERS
const onLoginSignup = (): void => {
    trackLoginSignupButton();
    openAuthModal();
};

const onLogout = (): void => {
    trackLogout();
    logout();
};
</script>

<template>
    <Transition
        enter-from-class="translate-x-full"
        leave-to-class="translate-x-full"
        :enter-active-class="transitionClass"
        :leave-active-class="closeTransition ? transitionClass : ''"
        @after-leave="resetCloseTransition"
    >
        <div
            v-if="openModel"
            class="fixed top-(--header-total-height) z-30 h-[calc(100vh-var(--header-total-height))] w-screen overflow-y-auto bg-white"
        >
            <div class="divi min-h-full divide-y divide-neutral-300">
                <!-- Navigation Menu -->
                <div class="space-y-2 p-4">
                    <UNavigationMenu color="neutral" arrow orientation="vertical" :items="navigationMenuItems" :ui="navigationMenuUi" />
                </div>

                <!-- Account Menu -->
                <div class="space-y-2 p-4">
                    <template v-if="isLoggedIn">
                        <div class="min-w-0 p-2 text-left font-bold">
                            <p>{{ tt('common.yourAccount') }}:</p>
                            <p class="text-primary truncate">
                                {{ data?.user?.email }}
                            </p>
                        </div>

                        <UNavigationMenu color="neutral" arrow orientation="vertical" :items="accountMenuItems" :ui="navigationMenuUi" />

                        <USeparator class="opacity-75" color="neutral" />

                        <button
                            class="flex w-full items-center justify-between rounded-md p-2 text-left font-medium"
                            @click="withMenuClose(onLogout)"
                        >
                            {{ tt('common.logout') }}
                            <UIcon class="text-xl" name="lucide:log-out" />
                        </button>
                    </template>

                    <UButton v-else :label="tt('common.loginSignup')" size="xl" block @click="withMenuClose(onLoginSignup)" />
                </div>

                <div class="flex p-4">
                    <div class="flex w-full items-center justify-between rounded-md p-2 text-left font-medium">
                        <span> {{ tt('common.language') }}:</span>
                        <LangSwitch />
                    </div>
                </div>
            </div>
        </div>
    </Transition>
</template>
